import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, Platform, Animated, ScrollView } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import FormInput from '../components/FormInput';
import PrimaryButton from '../components/PrimaryButton';
import { authService } from '../services/authService';

// Import character images
const characterWatch = require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Watch_v01_SL.png');

const MaintenanceScreen = () => {
  const { palette, mode } = useTheme();
  const { navigateTo } = useNavigation();
  
  // Admin login state
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);
  const [adminLoginError, setAdminLoginError] = useState('');

  // Animations
  const cardScale = useRef(new Animated.Value(0.9)).current;
  const cardOpacity = useRef(new Animated.Value(0)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(cardScale, {
        toValue: 1,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.timing(cardOpacity, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start();

    // Pulsing animation for the status indicator
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.05,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, []);

  const handleAdminLogin = async () => {
    if (!adminEmail.trim() || !adminPassword.trim()) {
      setAdminLoginError('Please enter both email and password');
      return;
    }

    setAdminLoginError('');
    setAdminLoginLoading(true);

    try {
      const result = await authService.login(adminEmail.trim(), adminPassword);
      
      if (result.error) {
        setAdminLoginError(result.error);
      } else if (result.user) {
        if (result.user.userType === 'admin') {
          navigateTo('MyDashboard');
        } else {
          setAdminLoginError('This account does not have admin access');
          await authService.logout();
        }
      }
    } catch (err) {
      setAdminLoginError('An unexpected error occurred. Please try again.');
      console.error('Admin login error:', err);
    } finally {
      setAdminLoginLoading(false);
    }
  };

  return (
    <ScrollView 
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.container}>
        <Animated.View style={[
          styles.mainCard,
          {
            opacity: cardOpacity,
            transform: [{ scale: cardScale }],
          }
        ]}>
          {/* Character Image */}
          <Image
            source={characterWatch}
            style={styles.characterImage}
            resizeMode="contain"
          />

          {/* Status Badge with pulse */}
          <Animated.View style={[
            styles.badge,
            { transform: [{ scale: pulseAnim }] }
          ]}>
            <View style={styles.statusDot} />
            <Text style={styles.badgeText}>MAINTENANCE IN PROGRESS</Text>
          </Animated.View>

          {/* Title */}
          <Text style={styles.title}>
            We'll Be Right Back
          </Text>
          
          <Text style={styles.description}>
            We're performing scheduled maintenance to improve your experience. 
            Our team is working hard to get things back online as quickly as possible.
          </Text>

          {/* Status Card */}
          <View style={styles.statusCard}>
            <View style={styles.statusRow}>
              <View style={styles.statusItem}>
                <Text style={styles.statusIcon}>⏱️</Text>
                <Text style={styles.statusLabel}>ESTIMATED TIME</Text>
                <Text style={styles.statusValue}>Brief Downtime</Text>
              </View>
              <View style={styles.statusDivider} />
              <View style={styles.statusItem}>
                <Text style={styles.statusIcon}>🔧</Text>
                <Text style={styles.statusLabel}>STATUS</Text>
                <Text style={[styles.statusValue, { color: '#DC2626' }]}>In Progress</Text>
              </View>
            </View>
          </View>

          <Text style={styles.thankYou}>
            Thank you for your patience. We appreciate your understanding.
          </Text>
        </Animated.View>

        {/* Admin Login Section */}
        <View style={styles.adminSection}>
          {!showAdminLogin ? (
            <TouchableOpacity
              style={styles.adminLink}
              onPress={() => setShowAdminLogin(true)}
            >
              <Text style={styles.adminLinkText}>
                Admin Access
              </Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.adminLoginCard}>
              <Text style={styles.adminLoginTitle}>
                Admin Login
              </Text>
              <Text style={styles.adminLoginSubtitle}>
                Sign in with admin credentials to bypass
              </Text>
              
              <FormInput
                placeholder="Admin email"
                value={adminEmail}
                onChangeText={(text) => {
                  setAdminEmail(text);
                  setAdminLoginError('');
                }}
                keyboardType="email-address"
                autoCapitalize="none"
                editable={!adminLoginLoading}
                style={styles.adminInput}
              />
              
              <FormInput
                placeholder="Password"
                value={adminPassword}
                onChangeText={(text) => {
                  setAdminPassword(text);
                  setAdminLoginError('');
                }}
                secureTextEntry
                editable={!adminLoginLoading}
                onSubmit={handleAdminLogin}
                style={styles.adminInput}
              />

              {adminLoginError ? (
                <Text style={styles.adminErrorText}>{adminLoginError}</Text>
              ) : null}

              <View style={styles.adminButtonRow}>
                <TouchableOpacity
                  style={styles.adminCancelButton}
                  onPress={() => {
                    setShowAdminLogin(false);
                    setAdminEmail('');
                    setAdminPassword('');
                    setAdminLoginError('');
                  }}
                >
                  <Text style={styles.adminCancelText}>Cancel</Text>
                </TouchableOpacity>
                
                <PrimaryButton
                  title={adminLoginLoading ? 'Signing in...' : 'Sign In'}
                  onPress={handleAdminLogin}
                  loading={adminLoginLoading}
                  disabled={adminLoginLoading}
                  style={styles.adminLoginButton}
                />
              </View>
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 60,
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  mainCard: {
    width: '100%',
    maxWidth: 550,
    backgroundColor: '#FFE66D',
    borderRadius: 24,
    borderWidth: 4,
    borderColor: '#000000',
    padding: 40,
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '8px 10px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  characterImage: {
    width: 160,
    height: 160,
    marginBottom: 16,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DC2626',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 3,
    borderColor: '#000000',
    marginBottom: 16,
    gap: 10,
    ...Platform.select({
      web: {
        boxShadow: '3px 3px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#FFFFFF',
  },
  badgeText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    letterSpacing: 1.5,
    color: '#FFFFFF',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 36,
    fontFamily: 'Chivo',
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 42,
    color: '#000000',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(255,255,255,0.5)',
      },
    }),
  },
  description: {
    fontSize: 16,
    fontFamily: 'Archivo',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 24,
    color: 'rgba(0,0,0,0.8)',
    maxWidth: 420,
  },
  statusCard: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    padding: 24,
    borderRadius: 12,
    borderWidth: 4,
    borderColor: '#000000',
    marginBottom: 24,
    ...Platform.select({
      web: {
        boxShadow: '5px 5px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusItem: {
    flex: 1,
    alignItems: 'center',
  },
  statusIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  statusLabel: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: 'rgba(0,0,0,0.6)',
  },
  statusValue: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
    color: '#000000',
  },
  statusDivider: {
    width: 3,
    height: 60,
    backgroundColor: '#000000',
    marginHorizontal: 24,
    borderRadius: 2,
  },
  thankYou: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    fontStyle: 'italic',
    color: 'rgba(0,0,0,0.7)',
  },
  // Admin section
  adminSection: {
    marginTop: 32,
    alignItems: 'center',
    width: '100%',
    maxWidth: 550,
  },
  adminLink: {
    padding: 12,
  },
  adminLinkText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.6)',
    textDecorationLine: 'underline',
  },
  adminLoginCard: {
    width: '100%',
    padding: 24,
    borderRadius: 12,
    borderWidth: 4,
    borderColor: '#000000',
    backgroundColor: '#4ECDC4',
    gap: 16,
    ...Platform.select({
      web: {
        boxShadow: '6px 8px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  adminLoginTitle: {
    fontSize: 22,
    fontFamily: 'Chivo',
    fontWeight: '700',
    textAlign: 'center',
    color: '#000000',
  },
  adminLoginSubtitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    marginBottom: 8,
    color: 'rgba(0,0,0,0.7)',
  },
  adminInput: {
    marginBottom: 0,
  },
  adminErrorText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: '#FFFFFF',
    backgroundColor: '#DC2626',
    textAlign: 'center',
    padding: 8,
    borderRadius: 8,
  },
  adminButtonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  adminCancelButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    borderWidth: 3,
    borderColor: '#000000',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '3px 3px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  adminCancelText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: '#000000',
  },
  adminLoginButton: {
    flex: 1,
    marginTop: 0,
    paddingVertical: 14,
    paddingHorizontal: 24,
  },
});

export default MaintenanceScreen;
